import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getStoreUrl } from '../utils/storeUrls';
import { isIOSInstagramBrowser, storeLinkProps, openInExternalBrowser } from '../utils/inAppBrowser';

// Helper function to get iOS store URL for a specific language
const getIosStoreUrl = (language: string): string => {
  const storeConfig = {
    en: "https://apps.apple.com/se/app/notely-ai/id6740462619?l=en-GB&platform=iphone",
    es: "https://apps.apple.com/es/app/notely-ai/id6740462619?l=es-ES&platform=iphone",
    fr: "https://apps.apple.com/fr/app/notely-ai/id6740462619?l=fr-FR&platform=iphone",
    de: "https://apps.apple.com/de/app/notely-ai/id6740462619?l=de-DE&platform=iphone",
    pt: "https://apps.apple.com/pt/app/notely-ai/id6740462619?l=pt-PT&platform=iphone",
    vi: "https://apps.apple.com/vn/app/notely-ai/id6740462619?l=vi&platform=iphone",
    sv: "https://apps.apple.com/se/app/notely-ai/id6740462619?l=sv&platform=iphone",
    ro: "https://apps.apple.com/ro/app/notely-ai/id6740462619?l=ro&platform=iphone",
  };
  return storeConfig[language as keyof typeof storeConfig] || storeConfig.en;
};

// Helper function to get Android store URL for a specific language
const getAndroidStoreUrl = (language: string): string => {
  return `https://play.google.com/store/apps/details?id=com.cstate.notelyapp&hl=${language}`;
};

interface DownloadRedirectProps {
  forceLanguage?: string;
}

const DownloadRedirect: React.FC<DownloadRedirectProps> = ({ forceLanguage }) => {
  const [isRedirecting, setIsRedirecting] = useState(true);
  const [showFallback, setShowFallback] = useState(false);
  const { t, i18n } = useTranslation();

  const supportedLocales = ['en', 'es', 'fr', 'de', 'pt', 'vi', 'sv', 'ro'];

  useEffect(() => {
    const redirect = () => {
      try {
        // Use forceLanguage if provided, otherwise detect user's language
        const detectedLanguage = forceLanguage || i18n.language;
        const targetLanguage = supportedLocales.includes(detectedLanguage) ? detectedLanguage : 'en';
        
        const storeUrl = getStoreUrl(targetLanguage);
        
        // The iOS Instagram webview blocks navigations to apps.apple.com, so hand
        // the URL to the system browser through Instagram's own extbrowser scheme.
        const handedOff = openInExternalBrowser(storeUrl);

        if (!handedOff) {
          // Redirect to the appropriate store
          window.location.href = storeUrl;
        }
        
        // Show fallback after a delay in case redirect fails
        setTimeout(() => {
          setShowFallback(true);
          setIsRedirecting(false);
        }, handedOff ? 1500 : 3000);
      } catch (error) {
        console.error('Error during redirect:', error);
        setShowFallback(true);
        setIsRedirecting(false);
      }
    };

    redirect();
  }, [forceLanguage, i18n.language, supportedLocales]);

  // Get the target language for fallback URLs
  const detectedLanguage = forceLanguage || i18n.language;
  const targetLanguage = supportedLocales.includes(detectedLanguage) ? detectedLanguage : 'en';

  // Inside the iOS Instagram webview, plain store links are blocked; storeLinkProps
  // routes the fallback buttons through Instagram's external-browser scheme too.
  const inInstagram = isIOSInstagramBrowser();

  if (isRedirecting && !showFallback) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-semibold mb-4">{t('downloadRedirect.redirecting')}</div>
          <div className="text-gray-400">{t('downloadRedirect.takingYou')}</div>
        </div>
      </div>
    );
  }

  // Fallback UI for when redirect doesn't work or for desktop users
  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <h1 className="text-3xl font-bold mb-6">{t('downloadRedirect.downloadTitle')}</h1>
        <p className="text-gray-400 mb-8">{t('downloadRedirect.choosePlatform')}</p>
        
        <div className="space-y-4">
          <a 
            {...storeLinkProps(getIosStoreUrl(targetLanguage))}
            className="block bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
          >
            {t('downloadRedirect.downloadIos')}
          </a>
          
          <a 
            {...storeLinkProps(getAndroidStoreUrl(targetLanguage))}
            className="block bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
          >
            {t('downloadRedirect.downloadAndroid')}
          </a>
        </div>
        
        <p className="text-sm text-gray-500 mt-6">
          {inInstagram ? t('downloadRedirect.instagramNotice') : t('downloadRedirect.notRedirected')}
        </p>
      </div>
    </div>
  );
};

export default DownloadRedirect;